import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useView } from '../../common/View/ViewContext';
import { useTransition } from '../../common/Transition/TransitionContext';
import './Header.css';

interface HeaderProps {
  currentPage?: 'home' | 'project' | 'profile';
}

const Header: React.FC<HeaderProps> = ({ currentPage = 'home' }) => {
  const { currentView, setCurrentView, isTransitioning, setIsTransitioning } = useView();
  const { isTransitioning: isPageTransitioning } = useTransition();
  const navigate = useNavigate();
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isFadingIn, setIsFadingIn] = useState(false);

  console.log('🔄 Header component rendered with currentPage:', currentPage);

  // currentPageが変更されたときのフェード処理
  useEffect(() => {
    if (currentPage !== 'home') {
      // ホーム以外のページに遷移する場合はフェードアウト
      setIsFadingOut(true);
      setIsFadingIn(false);
      // フェードアウトアニメーション完了後にDOMから削除
      const timer = setTimeout(() => {
        setIsFadingOut(false);
      }, 300); // transitionの時間と同じ
      return () => clearTimeout(timer);
    } else {
      // ホームページに戻る場合はフェードイン
      setIsFadingOut(false);
      setIsFadingIn(true);
      // 次のフレームでフェードイン開始
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsFadingIn(false);
        });
      });
    }
  }, [currentPage]);

  // OSADAクリックハンドラー（新規追加）
  const handleOSADAClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    // 既にホームページにいる場合は、titleビューに切り替え
    if (currentPage === 'home') {
      if (currentView === 'title') {
        // 既にtitleビューの場合は何もしない
        return;
      }
      
      // filmビューからtitleビューへ切り替え
      if (isTransitioning) return;
      
      console.log('Switching from film to title view');
      
      try {
        setIsTransitioning(true);
        
        // フェードアウト
        const homeElement = document.querySelector('.home');
        if (homeElement) {
          homeElement.classList.add('view-transitioning');
        }
        
        // フェードアウト完了を待つ
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // titleビューに変更
        setCurrentView('title');
        
        // 少し待ってからフェードイン
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // フェードイン
        if (homeElement) {
          homeElement.classList.remove('view-transitioning');
        }
        
        setIsTransitioning(false);
        
        console.log('View switch completed to title');
      } catch (error) {
        console.error('View switch failed:', error);
        setIsTransitioning(false);
      }
    } else {
      // 他のページからホームページに遷移する場合
      
      // まずtitleビューに設定してからナビゲート
      setCurrentView('title');
      
      if (currentPage === 'profile') {
        // プロフィールページからの場合はフェードトランジション
        if (isPageTransitioning || isTransitioning) return;
        
        console.log('Profile page: Fading out and transitioning to home with title view');
        
        try {
          setIsTransitioning(true);
          
          // プロフィールページの画像とコンテンツをフェードアウト
          const imageElement = document.querySelector('.profile-image-section');
          const contentElement = document.querySelector('.profile-content-section');
          
          if (imageElement && contentElement) {
            imageElement.classList.add('content-transitioning');
            contentElement.classList.add('content-transitioning');
          }
          
          // フェードアウト完了を待つ
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // ホームページに遷移
          navigate('/');
          
          // 少し待ってからトランジション状態をリセット
          setTimeout(() => {
            setIsTransitioning(false);
          }, 100);
          
          console.log('Successfully transitioned to home with title view');
        } catch (error) {
          console.error('Transition from profile to home failed:', error);
          setIsTransitioning(false);
        }
      } else if (currentPage === 'project') {
        // プロジェクトページからの場合はシンプルに直接遷移
        navigate('/');
      } else {
        // その他のページからは直接遷移
        navigate('/');
      }
    }
  };

  // ビュー切り替えハンドラー（既存のまま）
  const handleViewChange = async (newView: 'title' | 'film') => {
    // プロフィールページの場合：フェードアウト→ビュー設定→ホームページに遷移
    if (currentPage === 'profile') {
      if (isPageTransitioning || isTransitioning) return;
      
      console.log(`Profile page: Fading out content and setting view to ${newView}`);
      
      try {
        setIsTransitioning(true);
        
        // プロフィールページの画像とコンテンツをフェードアウト
        const imageElement = document.querySelector('.profile-image-section');
        const contentElement = document.querySelector('.profile-content-section');
        
        if (imageElement && contentElement) {
          imageElement.classList.add('content-transitioning');
          contentElement.classList.add('content-transitioning');
        }
        
        // フェードアウト完了を待つ
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // ビューを設定
        setCurrentView(newView);
        
        // ホームページに遷移
        navigate('/');
        
        // 少し待ってからトランジション状態をリセット
        setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
        
        console.log(`Successfully transitioned to home with ${newView} view`);
      } catch (error) {
        console.error('Transition from profile to home failed:', error);
        setIsTransitioning(false);
      }
      return;
    }

    // ホームページの場合：既存のビュー切り替え処理
    if (currentView === newView || isTransitioning) return;

    console.log(`Switching view from ${currentView} to ${newView}`);
    
    try {
      setIsTransitioning(true);
      
      // フェードアウト
      const homeElement = document.querySelector('.home');
      if (homeElement) {
        homeElement.classList.add('view-transitioning');
      }
      
      // フェードアウト完了を待つ
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // ビュー変更
      setCurrentView(newView);
      
      // 少し待ってからフェードイン
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // フェードイン
      if (homeElement) {
        homeElement.classList.remove('view-transitioning');
      }
      
      setIsTransitioning(false);
      
      console.log(`View switch completed to ${newView}`);
    } catch (error) {
      console.error('View switch failed:', error);
      setIsTransitioning(false);
    }
  };

  // PROFILEクリックハンドラー（既存のまま）
  const handleProfileClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    // すでにプロフィールページにいる場合は何もしない
    if (currentPage === 'profile') return;
    
    // ホームページからの場合はフェードトランジション
    if (currentPage === 'home') {
      if (isPageTransitioning || isTransitioning) return;
      
      console.log('Home page: Fading out and transitioning to profile');
      
      try {
        setIsTransitioning(true);
        
        // ホームページをフェードアウト
        const homeElement = document.querySelector('.home');
        if (homeElement) {
          homeElement.classList.add('page-transitioning');
        }
        
        // フェードアウト完了を待つ
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // プロフィールページに遷移
        navigate('/profile');
        
        // 少し待ってからトランジション状態をリセット
        setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
        
        console.log('Successfully transitioned to profile');
      } catch (error) {
        console.error('Transition to profile failed:', error);
        setIsTransitioning(false);
      }
    } else if (currentPage === 'project') {
      // プロジェクトページからの場合はシンプルに直接遷移
      navigate('/profile');
    } else {
      // その他のページからは直接遷移
      navigate('/profile');
    }
  };

  return (
    <header className="header">
      <div className="header-left">
        <h1 className="site-title">
          {/* ホームページではホバーエフェクトのみ、他のページではクリックでホームに戻る */}
          <button
            onClick={currentPage !== 'home' ? handleOSADAClick : undefined}
            className={`site-title-link hover-text-effect ${currentPage === 'home' ? 'disabled-hover' : ''}`}
            style={{
              background: 'none',
              border: 'none',
              cursor: currentPage !== 'home' ? 'pointer' : 'default',
              padding: 0,
              margin: 0,
              font: 'inherit',
              color: 'inherit',
              textDecoration: 'none',
              width: 'auto',
              minWidth: 'max-content'
            }}
          >
            <span className="hover-text-top">OSADA</span>
            <span className="hover-text-bottom">BACK HOME</span>
            {/* 幅確保用の非表示テキスト */}
            <span aria-hidden="true" style={{
              visibility: 'hidden',
              height: 0,
              overflow: 'hidden',
              display: 'block',
              whiteSpace: 'nowrap'
            }}>BACK HOME</span>
          </button>
        </h1>
      </div>
      
      <nav className="header-center">
        {/* ホームページのみでTITLE / IMAGEを表示（フェード付き） */}
        {(currentPage === 'home' || isFadingOut) && (
          <div className={`view-toggle ${isFadingOut ? 'fading-out' : ''} ${isFadingIn ? 'fading-in' : ''}`}>
            <button
              className={`view-toggle-btn ${currentView === 'title' ? 'active' : ''}`}
              onClick={() => handleViewChange('title')}
              disabled={isTransitioning}
            >
              TITLE
            </button>
            <span className="view-separator">/</span>
            <button
              className={`view-toggle-btn ${currentView === 'film' ? 'active' : ''}`}
              onClick={() => handleViewChange('film')}
              disabled={isTransitioning}
            >
              IMAGE
            </button>
          </div>
        )}
      </nav>
      
      <nav className="header-right">
        <button
          className={`view-toggle-btn about-btn hover-text-effect ${currentPage === 'profile' ? 'active disabled-hover' : ''}`}
          onClick={currentPage !== 'profile' ? handleProfileClick : undefined}
          style={{
            background: 'none',
            border: 'none',
            cursor: currentPage !== 'profile' ? 'pointer' : 'default'
          }}
        >
          <span className="hover-text-top">ABOUT</span>
          <span className="hover-text-bottom">ABOUT</span>
        </button>
      </nav>
    </header>
  );
};

export default Header;